import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Settings, Psychology, Send, Group, Key, ToggleOn } from '@mui/icons-material';
import { WhatsappConfig } from '../../services/whatsappService';

interface Props {
  config: WhatsappConfig;
  loading: boolean;
  onChange: (config: WhatsappConfig) => void;
  onSave: () => void;
  onTestConnection: () => void;
  onOpenTestMessage: () => void;
  onOpenGroupPicker: () => void;
}

const WhatsappConfigPanel: React.FC<Props> = ({
  config,
  loading,
  onChange,
  onSave,
  onTestConnection,
  onOpenTestMessage,
  onOpenGroupPicker,
}) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Koneksi StarSender
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Kredensial dari dashboard StarSender. Simpan konfigurasi sebelum mengirim pesan.
      </Typography>

      <Stack spacing={2.5}>
        <TextField
          fullWidth
          label="Instance ID"
          value={config.instanceId}
          onChange={(e) => onChange({ ...config, instanceId: e.target.value })}
          required
          size="small"
        />

        <TextField
          fullWidth
          label="API Key"
          type="password"
          value={config.apiKey}
          onChange={(e) => onChange({ ...config, apiKey: e.target.value })}
          required
          size="small"
          helperText="API Key dari StarSender (hash panjang atau format key:value)"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Key fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Grup WhatsApp (opsional)"
          value={config.groupId || ''}
          onChange={(e) => onChange({ ...config, groupId: e.target.value })}
          size="small"
          helperText="Nama grup WhatsApp (pilih dari daftar — bukan ID numerik)"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Pilih dari daftar grup">
                  <IconButton size="small" onClick={onOpenGroupPicker} edge="end">
                    <Group />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: config.isActive ? 'success.light' : 'divider',
            bgcolor: config.isActive ? 'success.50' : 'grey.50',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={config.isActive}
                onChange={(e) => onChange({ ...config, isActive: e.target.checked })}
                color="success"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  WhatsApp aktif
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Nonaktifkan untuk menghentikan semua pengiriman otomatis
                </Typography>
              </Box>
            }
          />
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button variant="contained" onClick={onSave} disabled={loading} startIcon={<Settings />}>
            Simpan
          </Button>
          <Button variant="outlined" onClick={onTestConnection} disabled={loading} startIcon={<Psychology />}>
            Test Koneksi
          </Button>
          <Button variant="outlined" onClick={onOpenTestMessage} disabled={loading} startIcon={<Send />}>
            Kirim Test
          </Button>
        </Stack>

        {!config.apiKey && (
          <Alert severity="info" icon={<ToggleOn />}>
            Isi dan simpan API Key terlebih dahulu agar reminder tagihan dapat dikirim.
          </Alert>
        )}
      </Stack>
    </CardContent>
  </Card>
);

export default WhatsappConfigPanel;
